import { getClient, getClientDocumentsInfo, getClientPaymentsInfo } from 'axios/clientApi';
import { getOrderCommentsInfo, getOrderInfo, orderAssignToOperator } from 'axios/orderApi';
import ClientFilesTab from 'components/Workplaces/Order/OrderInfo/Tabs/ClientFilesTab';
import ClientTab from 'components/Workplaces/Order/OrderInfo/Tabs/ClientTab';
import CommentsTab from 'components/Workplaces/Order/OrderInfo/Tabs/CommentsTab';
import DocumentsTab from 'components/Workplaces/Order/OrderInfo/Tabs/DocumentsTab';
import OrderTab from 'components/Workplaces/Order/OrderInfo/Tabs/OrderTab';
import PaymentsTab from 'components/Workplaces/Order/OrderInfo/Tabs/PaymentsTab';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { useNotifyContext } from 'providers/NotifyProvider';
import { usePreorderInfoContext } from 'providers/PreorderInfoProvider';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Container, FormGroup, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import 'scss/Main.scss';
import 'components/Workplaces/Order/OrderInfo/OrderInfo.scss';
import { PreorderDetailsGeneralInfo } from 'models/api/preorder';
import { useToastContext } from '@finbeat/react-toolkit';

const PreorderInfo = ({
    generalInfo,
    modalRef,
    cancelAction,
}: {
    isModal?: boolean;
    generalInfo: PreorderDetailsGeneralInfo;
    modalRef?: RefObject<HTMLElement>;
    cancelAction?: (dirty?: boolean) => void;
}) => {
    const className = 'order-info';
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const api = useApi();
    const containerRef = useRef(null);
    const [activeTab, setActiveTab] = useState('');
    const { setRequestedDocs } = usePreorderInfoContext();
    const { submitOrderMutation } = usePreorderInfoContext();
    const [isNextSubmitted] = useState<boolean>(false);
    const { addSubscription, removeSubscription } = useNotifyContext();
    const { addToast } = useToastContext();

    useEffect(() => {
        if (isNextSubmitted) {
            submitOrderMutation.mutate(
                generalInfo.orderGeneral.canPassToScoring ? 'next' : 'checkdocs'
            );
        }
    }, [isNextSubmitted]);

    useEffect(() => {
        if (generalInfo.orderGeneral.statusCode === 'checkdocs') setActiveTab('clientFiles');
        else setActiveTab('order');
    }, [generalInfo]);

    const assignToOperator = useMutation('assignToOperator', () =>
        orderAssignToOperator(api, generalInfo.orderGeneral.orderId ?? 0)
    );

    useEffect(() => assignToOperator.mutate(), []);

    useEffect(() => {
        addSubscription({
            key: 'onOrderStatusChange',
            type: 'OrderStatusChange',
            callback: (message) => {
                const info = JSON.parse(message);

                if (
                    info.OrderId === generalInfo.orderGeneral.orderId &&
                    info.Status === 'clcancel'
                ) {
                    addToast({
                        type: 'info',
                        description: t('BackOfficeOrderCancelledMsg'),
                        autoDeleteTime: null,
                    });
                    cancelAction?.(false);
                }
            },
        });

        return () => {
            removeSubscription('onOrderStatusChange');
        };
    }, [addSubscription, removeSubscription, addToast, cancelAction]);

    return (
        <Container className={className}>
            <FormGroup className={`${className}__nav-card`}>
                <Nav className={`${className}__nav-tabs`} tabs>
                    {hasRole('clientsGetById') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('client')}
                                active={activeTab === 'client'}
                            >
                                {t('BackOfficeClient')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetInfo') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('order')}
                                active={activeTab === 'order'}
                            >
                                {t('BackOfficeOrder')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('clientsGetPaymentInfo') && generalInfo.orderGeneral.orderId && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('payments')}
                                active={activeTab === 'payments'}
                            >
                                {t('BackOfficePayments')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('clientsGetClientFiles') && generalInfo.orderGeneral.clientId && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('clientFiles')}
                                active={activeTab === 'clientFiles'}
                            >
                                {t('BackOfficeClientFiles')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetDocuments') && generalInfo.orderGeneral.orderId && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('documents')}
                                active={activeTab === 'documents'}
                            >
                                {t('BackOfficeDocuments')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetCommentsInfo') && generalInfo.orderGeneral.orderId && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('comments')}
                                active={activeTab === 'comments'}
                            >
                                {t('BackOfficeComments')}
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
            </FormGroup>
            <div className={`${className}__content-card custom-scrollbar mb-3`} ref={containerRef}>
                <TabContent activeTab={activeTab}>
                    {hasRole('clientsGetById') && (
                        <TabPane tabId="client">
                            <ClientTab
                                clientInfoQueryFn={() =>
                                    getClient(
                                        api,
                                        generalInfo.orderGeneral.clientId ?? 0,
                                        generalInfo.orderGeneral.orderId
                                    )
                                }
                                isEditable={
                                    generalInfo.orderGeneral.canEditOrder &&
                                    hasRole('clientsUpdateDataOnManualCheck')
                                }
                                manualCheck={true}
                                modalRef={modalRef}
                                orderId={generalInfo.orderGeneral.orderId}
                                clientId={generalInfo.orderGeneral.clientId ?? 0}
                                isOpen={activeTab === 'client'}
                            />
                        </TabPane>
                    )}
                    {hasRole('orderGetInfo') && (
                        <TabPane tabId="order">
                            <OrderTab
                                orderInfoQueryFn={() =>
                                    getOrderInfo(api, generalInfo.orderGeneral.orderId ?? 0)
                                }
                                isOpen={activeTab === 'order'}
                                container={containerRef}
                            />
                        </TabPane>
                    )}
                    {hasRole('clientsGetPaymentInfo') &&
                        generalInfo.orderGeneral.orderId &&
                        generalInfo.orderGeneral.clientId && (
                            <TabPane tabId="payments">
                                <PaymentsTab
                                    paymentsQueryFn={() =>
                                        getClientPaymentsInfo(
                                            api,
                                            generalInfo.orderGeneral.clientId ?? 0,
                                            generalInfo.orderGeneral.orderId ?? 0
                                        )
                                    }
                                    isOpen={activeTab === 'payments'}
                                    container={containerRef}
                                />
                            </TabPane>
                        )}
                    {hasRole('clientsGetClientFiles') &&
                        generalInfo.orderGeneral.orderId &&
                        generalInfo.orderGeneral.clientId && (
                            <TabPane tabId="clientFiles">
                                <ClientFilesTab
                                    isEditable={generalInfo.orderGeneral.canEditOrder}
                                    documentsQueryFn={() =>
                                        getClientDocumentsInfo(
                                            api,
                                            generalInfo.orderGeneral.clientId ?? 0
                                        )
                                    }
                                    isOpen={activeTab === 'clientFiles'}
                                    canRequestingDocs={generalInfo.orderGeneral.canUpdDocs}
                                    modalRef={modalRef}
                                    clientId={generalInfo.orderGeneral.clientId}
                                    orderId={generalInfo.orderGeneral.orderId}
                                    setRequestedDocs={setRequestedDocs}
                                />
                            </TabPane>
                        )}
                    {hasRole('orderGetDocuments') && generalInfo.orderGeneral.orderId && (
                        <TabPane tabId="documents">
                            <DocumentsTab
                                id={generalInfo.orderGeneral.orderId}
                                isOpen={activeTab === 'documents'}
                            />
                        </TabPane>
                    )}
                    {hasRole('orderGetCommentsInfo') && generalInfo.orderGeneral.orderId && (
                        <TabPane tabId="comments">
                            <CommentsTab
                                orderId={generalInfo.id}
                                commentsQueryFn={() =>
                                    getOrderCommentsInfo(api, generalInfo.orderGeneral.orderId ?? 0)
                                }
                                isOpen={activeTab === 'comments'}
                            />
                        </TabPane>
                    )}
                </TabContent>
            </div>
        </Container>
    );
};

export default PreorderInfo;
